import { Close, LocationOn, MyLocation } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import JobAlertPlaceHolder from "../../assets/images/JobAlertsPlaceholder.png";
import Loader from "../../common/Loader";
import {
  DELETE_EXTERNAL_JOB_ALERTS,
  DELETE_JOB_POST_ALERT,
  FETCH_EXTERNAL_JOB_ALERTS
} from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import DeleteConfirmation from "../common/DeleteConfirmation";
import Emptydata from "../common/Emptydata";
import AddJobAlertModal from "./AddJobAlertModal";
import { English } from "../../constant/EnglishFallback";

const NR_PER_PAGE = 20;

const MyJobAlertModal = ({ visible, onClose, onNewAlertCreated }) => {
  const { multiLanguageData, loginData } = useSelector((state) => state?.main);

  const LanguageData = multiLanguageData?.JobPostings || {};

  const {
    TEXT_MY_JOB_ALERTS = English?.JobPostings?.TEXT_MY_JOB_ALERTS,
    TEXT_GET_NOTIFICATIONS = English?.JobPostings?.TEXT_GET_NOTIFICATIONS,
    TEXT_NO_JOB_ALERTS_FOUND = English?.JobPostings?.TEXT_NO_JOB_ALERTS_FOUND,
    BUTTON_ADD_ALERT = English?.JobPostings?.BUTTON_ADD_ALERT,
    TEXT_WITHIN = English?.JobPostings?.TEXT_WITHIN,
    TEXT_KM = English?.JobPostings?.TEXT_KM,
    TEXT_TRADE_LABEL = English?.JobPostings?.TEXT_TRADE_LABEL,
    TEXT_TYPE = English?.JobPostings?.TEXT_TYPE,
    TEXT_CATEGORY_LABEL = English?.JobPostings?.TEXT_CATEGORY_LABEL,
    TEXT_DELETE_JOB_ALERT = English?.JobPostings?.TEXT_DELETE_JOB_ALERT,
    TEXT_DELETE_JOB_ALERT_CONFIRM = English?.JobPostings?.TEXT_DELETE_JOB_ALERT_CONFIRM,
    TEXT_YES_DELETE = English?.JobPostings?.TEXT_YES_DELETE,
    TEXT_DELETING = English?.JobPostings?.TEXT_DELETING
  } = LanguageData || {};

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [jobAlerts, setJobAlerts] = useState([]);
  const [pageNr, setPageNr] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Delete
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null); // { id, isExternal }
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Add alert
  const [showAddAlertModal, setShowAddAlertModal] = useState(false);

  useEffect(() => {
    if (visible) {
      setPageNr(1);
      setHasMore(true);
      fetchJobAlerts(1);
    }
  }, [visible]);

  // Fetch both internal + external alerts in parallel, tag each item, merge & sort by id desc
  const fetchJobAlerts = async (pageNumber = 1, isLoadMore = false) => {
    try {
      isLoadMore ? setIsLoadingMore(true) : setIsLoading(true);

      const userId = loginData?.userInformation?.UserId;
      const buildPayload = (isFetchAllowed) => ({
        PageNr: pageNumber,
        NrOfRecPerPage: NR_PER_PAGE,
        FullSearch: "",
        UserId: userId,
        IncludeRecordNr: true,
        TypeOfObjectReturned: "",
        FetchAllowedRecordsOnly: isFetchAllowed,
        SearchList: [{ userId: Number(userId) || 0 }],
        SortList: [{}]
      });
      const internalRes = await Postrequestcall(
        FETCH_EXTERNAL_JOB_ALERTS,
        buildPayload(false),
        loginData?.accessToken
      );

      const internalData = internalRes?.data?.data || [];

      const merged = [...internalData];
      const combinedTotal = internalRes?.data?.nrOfRecords || 0;

      const updated = isLoadMore ? [...jobAlerts, ...merged] : merged;
      setJobAlerts(updated);
      setHasMore(updated.length < combinedTotal);
      setPageNr(pageNumber);
    } catch (error) {
      console.error("Error fetching job alerts:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore && !isLoading) {
      fetchJobAlerts(pageNr + 1, true);
    }
  };

  const handleDeletePress = (item) => {
    setSelectedAlert(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAlert) return;
    try {
      setDeleteLoading(true);
      const { id, isExternal } = selectedAlert;

      console.log("Deleting alert with id:", id, "isExternal:", isExternal);
      const apiPath = isExternal ? DELETE_EXTERNAL_JOB_ALERTS : DELETE_JOB_POST_ALERT;

      const payload = {
        UserId: Number(loginData?.userInformation?.UserId) || 0,
        ReturnRecordId: false,
        ReturnRecordError: true,
        ...(isExternal ? { SoftDelete: true } : {}),
        DeleteList: [{ Id: id }]
      };

      const res = await Postrequestcall(apiPath, payload, loginData?.accessToken);

      if (res?.status === 200 || res?.status === 201 || res?.data?.status) {
        setJobAlerts((prev) =>
          prev.filter((item) => !(item.id === id && item.isExternal === isExternal))
        );
      }
    } catch (error) {
      console.error("Error deleting job alert:", error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirmation(false);
      setSelectedAlert(null);
    }
  };

  // --- Helpers ---
  const renderAddress = (alert) => {
    if (alert?.city && alert?.province) return `${alert.city}, ${alert.province}`;
    if (alert?.city) return alert.city;
    if (alert?.address?.trim()) return alert.address;
    return "";
  };

  const formatJobType = (jobType) => {
    if (!jobType) return null;
    const map = { FULLTIME: "Full-Time", PARTTIME: "Part-Time", CONTRACT: "Contract" };
    return map[jobType] || jobType;
  };

  // --- Card render ---
  const renderAlertCard = (item) => {
    const key = `${item.isExternal ? "ext" : "int"}-${item.id}`;
    const isExternal = item?.isExternal;

    // For internal alerts, use city/province; for external use item.location
    const locationText = isExternal
      ? item.location
      : item.city && item.province
        ? `${item.city}, ${item.province}`
        : item.city || "";

    return (
      <Card
        key={key}
        variant="outlined"
        sx={{ borderRadius: 2, mb: 1.5, borderColor: "#ddd", boxShadow: "none" }}>
        <Stack sx={{ alignItems: "flex-start", justifyContent: "space-between", p: 1.5 }} direction="row">
          <Stack spacing={0.75} flex={1}>
            {/* Location with icons */}
            {locationText && (
              <>
                <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.5}>
                  <LocationOn sx={{ fontSize: 17, color: "#888" }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#000" }}>
                    {locationText || "N/A"}
                  </Typography>
                </Stack>
                <Stack sx={{ alignItems: "center", pl: 0.3 }} direction="row" spacing={0.5}>
                  <MyLocation sx={{ fontSize: 12, color: "#888" }} />
                  <Typography variant="body2" sx={{ fontSize: 12, color: "#888" }}>
                    {`${TEXT_WITHIN} ${item?.radius || 0} ${TEXT_KM}`}
                  </Typography>
                </Stack>
              </>
            )}

            {/* Trade Name */}
            {item?.tradeName && (
              <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.75}>
                {!locationText && (
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14 }}>
                    {TEXT_TRADE_LABEL}
                  </Typography>
                )}

                <Chip
                  label={item.tradeName}
                  size="small"
                  sx={{
                    fontSize: 11,
                    height: 22,
                    backgroundColor: "#eee",
                    color: "#555",
                    ml: locationText ? 0 : 0.5
                  }}
                />
              </Stack>
            )}

            {/* Divider - show if there's Type or Category */}
            {(item?.jobType || item?.categoryType || item?.categoryName) && (
              <Divider sx={{ my: 0.5 }} />
            )}

            {/* Job Type */}
            {item?.jobType && (
              <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.75}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14 }}>
                  {TEXT_TYPE}
                </Typography>

                <Chip
                  label={formatJobType(item.jobType)}
                  size="small"
                  sx={{
                    fontSize: 11,
                    height: 22,
                    backgroundColor: "#eee",
                    color: "#555",
                    ml: 0.5
                  }}
                />
              </Stack>
            )}

            {/* Category */}
            {(item?.categoryType || item?.categoryName) && (
              <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.75}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14 }}>
                  {TEXT_CATEGORY_LABEL}
                </Typography>

                <Chip
                  label={
                    item.categoryType && item.categoryName
                      ? `${item.categoryType}: "${item.categoryName}"`
                      : item.categoryType || `"${item.categoryName}"`
                  }
                  size="small"
                  sx={{
                    fontSize: 11,
                    height: 22,
                    backgroundColor: "#eee",
                    color: "#555",
                    ml: 0.5
                  }}
                />
              </Stack>
            )}
          </Stack>

          {/* Delete button */}
          <IconButton size="small" onClick={() => handleDeletePress(item)} sx={{ mt: -0.5 }}>
            <Close fontSize="small" sx={{ color: "#888" }} />
          </IconButton>
        </Stack>
      </Card>
    );
  };

  return (
    <>
      <Modal open={visible} onClose={onClose}>
        <Box
          className="user-modal-responsive"
          sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
          {/* Header */}
          <Stack sx={{ alignItems: "center", justifyContent: "space-between", pb: 1, borderBottom: "1px solid #eee" }} direction="row">
            <Typography variant="h6" sx={{ fontSize: 18 }}>
              {TEXT_MY_JOB_ALERTS}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>

          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, mb: 1.5 }}>
            {TEXT_GET_NOTIFICATIONS}
          </Typography>

          {/* List */}
          <Box sx={{ flex: 1, overflowY: "auto" }} id="jobAlerts_scroll">
            {isLoading ? (
              <Stack sx={{ alignItems: "center", justifyContent: "center", height: 320 }}>
                <Loader />
              </Stack>
            ) : jobAlerts.length > 0 ? (
              <InfiniteScroll
                dataLength={jobAlerts.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  <Stack sx={{ py: 1,  alignItems: "center" }} >
                    <CircularProgress size={22} />
                  </Stack>
                }
                scrollableTarget="jobAlerts_scroll">
                {jobAlerts.map(renderAlertCard)}
              </InfiniteScroll>
            ) : (
              <Box className="empty-data-card">
                <Emptydata
                  icon={
                    <img
                      src={JobAlertPlaceHolder}
                      alt="JobPlaceHolder"
                      style={{
                        width: "400px",
                        height: "300px",
                        objectFit: "contain"
                      }}
                    />
                  }
                  firstText={TEXT_NO_JOB_ALERTS_FOUND}
                />
              </Box>
            )}
          </Box>

          {/* Footer – always show "Add alert" */}
          <Stack sx={{ justifyContent: "flex-end", pt: 1.5, borderTop: "1px solid #eee", mt: 1 }} direction="row">
            <Button variant="contained" onClick={() => setShowAddAlertModal(true)}>
              {BUTTON_ADD_ALERT}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {showDeleteConfirmation && (
        <DeleteConfirmation
          open={showDeleteConfirmation}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setSelectedAlert(null);
          }}
          headerText={TEXT_DELETE_JOB_ALERT}
          secondaryText={TEXT_DELETE_JOB_ALERT_CONFIRM}
          onBadgeClick={handleDeleteConfirm}
          actionBtnText={deleteLoading ? TEXT_DELETING : TEXT_YES_DELETE}
        />
      )}

      {showAddAlertModal && (
        <AddJobAlertModal
          open={showAddAlertModal}
          onCLose={() => setShowAddAlertModal(false)}
          onRefresh={() => {
            fetchJobAlerts(1);
            if (onNewAlertCreated) onNewAlertCreated();
          }}
        />
      )}
    </>
  );
};

export default MyJobAlertModal;
